/**
 * Which feedback belongs to the AI screen, and which to the Feedback module.
 *
 * Which component types are AI is DATA: each item of the `feedbackComponentType`
 * value set carries an `isAi` flag, read here through `useAiComponentTypes()`
 * and by the server through `codes_flagged` (`feedback_component_scope.py`).
 * Callers pass the flagged set in; this module only applies it.
 *
 * `FALLBACK_AI_COMPONENT_TYPES` is what the seed flags, and what these helpers
 * use when the caller has no set yet — the value set still loading, or a
 * database seeded before the flag existed. It deliberately mirrors the server's
 * fallback rather than the "null means no opinion, show everything" convention
 * the other value-set hooks use: showing everything in BOTH dropdowns would let
 * someone author a RAG definition from the Feedback module and then never find
 * it there again, because the server would file it under AI regardless.
 */

/** The seed's list, and the fallback. Mirrors FALLBACK_AI_COMPONENT_TYPES. */
export const FALLBACK_AI_COMPONENT_TYPES = [
  'PROMPT',
  'PROMPT_EXECUTION',
  'AI_MESSAGE',
  'AI_RESPONSE',
  'AI_SESSION',
  'RAG',
  'AGENT',
  'EVAL',
  'WORKFLOW',
  'DOC_EXTRACTION',
] as const;

/** Which window is asking. `undefined` means "everything". */
export type FeedbackScope = 'AI' | 'CORE';

/**
 * Is this component type an AI one?
 *
 * `aiTypes` is the flagged set from the value set; `null`/omitted falls back to
 * the constant above. Codes are compared upper-cased, so a set built from
 * hand-entered value-set rows still matches.
 */
export const isAiComponentType = (
  code?: string | null,
  aiTypes?: ReadonlySet<string> | null,
): boolean => {
  const value = String(code ?? '').trim().toUpperCase();
  if (!value) return false;
  if (!aiTypes) return (FALLBACK_AI_COMPONENT_TYPES as readonly string[]).includes(value);
  for (const t of aiTypes) {
    if (String(t).trim().toUpperCase() === value) return true;
  }
  return false;
};

/**
 * The component types a screen in this scope may choose from.
 *
 * CORE keeps everything that is not AI, INCLUDING an unrecognised code someone
 * added to the value set by hand: an unflagged type is a screen type until
 * somebody flags it, and silently dropping it from both dropdowns would make it
 * unusable with no error to explain why.
 */
export const optionsForScope = <T extends { value: string }>(
  options: T[],
  scope?: FeedbackScope,
  aiTypes?: ReadonlySet<string> | null,
): T[] => {
  if (!scope) return options;
  return options.filter((o) => isAiComponentType(o.value, aiTypes) === (scope === 'AI'));
};
